import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from 'react-router-dom'
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTachometer, faTasks, faEye, faPencil, faTrashCan, faVideo, faArrowLeft, faDumbbell, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useRecoilState } from 'recoil';

import { getSessionTemplateDetailAPI, putSessionTemplateUpdateAPI } from "../../../API/SessionTemplate";
import useLocalStorage from "../../../Hooks/useLocalStorage";
import DataDisplayRowText from "../../Reusable/DataDisplayRowText";
import PageLoadingContent from "../../Reusable/PageLoadingContent";
import { topAlertMessageState, topAlertStatusState } from "../../../AppState";


function SessionTemplateVideoSectionExerciseDetail() {
    ////
    //// URL parameters.
    ////

    const { id, vsIndex } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);

    ////
    //// Component states.
    ////

    const [setErrors] = useState({});
    const [sessionTemplate, setSessionTemplate] = useLocalStorage(id, "");
    const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] = useState(false);
    const [forceURL, setForceURL] = useState("");
    const [isFetching, setFetching] = useState(false);

    ////
    //// API.
    ////

    // --- Detail --- //

    function onSessionTemplateDetailSuccess(response){
        console.log("onSessionTemplateDetailSuccess: Starting...");
        setSessionTemplate(response);
    }

    function onSessionTemplateDetailError(apiErr) {
        console.log("onSessionTemplateDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionTemplateDetailDone() {
        console.log("onSessionTemplateDetailDone: Starting...");
        setFetching(false);
    }

    // --- Delete --- //

    function onSessionTemplateUpdateSuccess(response){
        console.log("onSessionTemplateUpdateSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("Session template deleted");
        setTimeout(() => {
            console.log("onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Upone success redirect user back to the `/users` list page.
        setForceURL("/session-template/" + id + "/video-sections");
    }

    function onSessionTemplateUpdateError(apiErr) {
        console.log("onSessionTemplateUpdateError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onSessionTemplateUpdateError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionTemplateUpdateDone() {
        console.log("onSessionTemplateUpdateDone: Starting...");
        setFetching(false);
    }

    ////
    //// Event handling.
    ////

    const onDeleteConfirmButtonClick = () => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        // Remove the item at this location.
        sessionTemplate.videoSections.splice(vsIndex, 1);

        putSessionTemplateUpdateAPI(
            sessionTemplate,
            onSessionTemplateUpdateSuccess,
            onSessionTemplateUpdateError,
            onSessionTemplateUpdateDone
        );
        setShowDeletionConfirmationModal(false);
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            setFetching(true);
            getSessionTemplateDetailAPI(
                id,
                onSessionTemplateDetailSuccess,
                onSessionTemplateDetailError,
                onSessionTemplateDetailDone
            );
        }

        return () => { mounted = false; }
    }, [id]);

    ////
    //// Component rendering.
    ////

    // Defensive code: The following code will extract the video section from the sessionTemplate, else return null.
    let videoSection, name, detail, exercises = null;
    if (sessionTemplate !== undefined && sessionTemplate !== null && sessionTemplate !== "") {
        videoSection = sessionTemplate.videoSections[vsIndex];
        if (videoSection !== undefined && videoSection !== null && videoSection !== "") {
            name = videoSection.name;
            detail = videoSection.detail;
            exercises = videoSection.exercises;
        }
    }
    console.log(name, detail, exercises); // For debugging purposes only.

    if (forceURL !== "") {
        return <Navigate to={forceURL}  />
    }

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/session-templates" aria-current="page"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session Templates</Link></li>
                            <li class=""><Link to={`/session-template/${id}/video-sections`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Details (Video Sections)</Link></li>
                            <li class="is-active"><Link aria-current="page"><FontAwesomeIcon className="fas" icon={faVideo} />&nbsp;Video Section</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/session-template/${id}/video-sections`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Details (Video Sections)</Link></li>
                        </ul>
                    </nav>

                    {/* Modals */}
                    <nav class="">
                        <div class={`modal ${showDeletionConfirmationModal ? 'is-active' : ''}`}>
                            <div class="modal-background"></div>
                            <div class="modal-card">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">Are you sure?</p>
                                    <button class="delete" aria-label="close" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}></button>
                                </header>
                                <section class="modal-card-body">
                                    You are about to delete this video section and all the data inside of it. This action is cannot be undone. Are you sure you would like to continue?
                                </section>
                                <footer class="modal-card-foot">
                                    <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                    <button class="button" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}>Cancel</button>
                                </footer>
                            </div>
                        </div>
                    </nav>

                    {/* Page */}
                    <nav class="box">
                        {/* Title + Options */}
                        <div class="columns">
                            <div class="column">
                                <p class="title is-4"><FontAwesomeIcon className="fas" icon={faVideo} />&nbsp;Video Section</p>
                            </div>
                            <div class="column has-text-right">

                                <Link to={`/session-template/${id}/video-section/${vsIndex}/edit`} class="button is-small is-warning is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit Video Section
                                </Link>
                                &nbsp;
                                <button onClick={(e, ses) => setShowDeletionConfirmationModal(true)} class="button is-small is-danger is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete Video Section
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div class="tabs is-medium">
                            <ul>
                                <li>
                                    <Link class="is-active" to={`/session-template/${id}/video-section/${vsIndex}`}><strong>Detail</strong></Link>
                                </li>
                                <li>
                                    <Link to={`/session-template/${id}/video-section/${vsIndex}/exercises`}>Exercises</Link>
                                </li>
                            </ul>
                        </div>

                        <p class="subtitle is-6"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Details</p>

                        {isFetching && <PageLoadingContent displayMessage={"Please wait"} />}

                        {!isFetching && <div class="container">
                            <DataDisplayRowText
                                label="Name"
                                name="name"
                                placeholder="Text input"
                                value={name}
                                maxWidth="350px"
                                disabled={true}
                            />

                            <DataDisplayRowText
                                label="Detail"
                                name="detail"
                                placeholder="Text input"
                                value={detail}
                                maxWidth="150px"
                                disabled={true}
                            />

                            <div class="columns">
                                <div class="column is-half">
                                    <Link to={`/session-template/${id}/video-sections`} class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Video Sections</Link>
                                </div>
                                <div class="column is-half has-text-right">
                                    <Link to={`/session-template/${id}/video-section/${vsIndex}/edit`} class="button is-warning is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link>
                                </div>
                            </div>

                        </div>}
                    </nav>
                </section>
            </div>
        </>
    );
}

export default SessionTemplateVideoSectionExerciseDetail;
